/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ViewGridIcon,
  RowsIcon,
  ViewHorizontalIcon,
  TableIcon,
} from '@/shared/ui/radix-icons';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { SegmentedControl } from '@/shared/ui';
import { MobileLayoutSwitcher } from '@/shared/ui/components/composed/mobile-layout-switcher';
import type { SegmentedControlItem } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { ViewMode, MobileKanbanLayout } from './ManageVisitsPage.helpers';

type VisitsToolbarProps = {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  mobileKanbanLayout: MobileKanbanLayout;
  onMobileKanbanLayoutChange: (layout: MobileKanbanLayout) => void;
};

const MOBILE_LAYOUT_ICONS: Record<MobileKanbanLayout, React.ReactNode> = {
  tabs: <ViewHorizontalIcon width={14} height={14} />,
  stack: <RowsIcon width={14} height={14} />,
  carousel: <ViewGridIcon width={14} height={14} />,
};

const MOBILE_LAYOUTS: MobileKanbanLayout[] = ['tabs', 'stack', 'carousel'];

/**
 * Toolbar for the visits page: a period select on the left and a unified
 * view-mode segmented control on the right.
 */
export const VisitsToolbar: React.FC<VisitsToolbarProps> = ({
  viewMode,
  onViewModeChange,
  mobileKanbanLayout,
  onMobileKanbanLayoutChange,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('features/visits');

  const desktopItems: SegmentedControlItem<ViewMode>[] = useMemo(
    () => [
      { value: 'kanban' as ViewMode, label: t('toolbar.kanban') },
      { value: 'table' as ViewMode, label: t('toolbar.table') },
    ],
    [t],
  );

  return (
    <div className="flex items-center justify-end gap-(--space-3) mt-(--space-4) mb-(--space-4)">
      {isMobile ? (
        <MobileSegment
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          mobileKanbanLayout={mobileKanbanLayout}
          onMobileKanbanLayoutChange={onMobileKanbanLayoutChange}
          t={t}
        />
      ) : (
        <SegmentedControl<ViewMode>
          value={viewMode}
          onChange={onViewModeChange}
          items={desktopItems}
          size="sm"
          ariaLabel={t('toolbar.viewSwitcher')}
          testId="view-mode-toggle"
        />
      )}
    </div>
  );
};

type MobileSegmentProps = {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  mobileKanbanLayout: MobileKanbanLayout;
  onMobileKanbanLayoutChange: (layout: MobileKanbanLayout) => void;
  t: (key: string) => string;
};

/**
 * Renders the mobile kanban layout switcher (tabs / stack / carousel) as a
 * segmented button group, activating kanban view when a layout is chosen.
 *
 * @param props - The current view mode, layout, and their change handlers plus `t`.
 * @returns The mobile layout segment control.
 */
function MobileSegment({
  viewMode,
  onViewModeChange,
  mobileKanbanLayout,
  onMobileKanbanLayoutChange,
  t,
}: MobileSegmentProps) {
  const labels: Record<MobileKanbanLayout, string> = {
    tabs: t('toolbar.tabs'),
    stack: t('toolbar.list'),
    carousel: t('toolbar.carousel'),
  };

  /* Kanban's three layouts plus the table, as one row. The table is not a
     kanban layout, which is why it carries a different icon and sits apart
     from the three in meaning even though the row joins them. */
  type Segment = MobileKanbanLayout | 'table';
  const value: Segment = viewMode === 'table' ? 'table' : mobileKanbanLayout;

  return (
    <MobileLayoutSwitcher<Segment>
      value={value}
      onChange={(next) => {
        if (next === 'table') { onViewModeChange('table'); return; }
        if (viewMode !== 'kanban') onViewModeChange('kanban');
        onMobileKanbanLayoutChange(next);
      }}
      ariaLabel={t('toolbar.viewSwitcher')}
      options={[
        ...MOBILE_LAYOUTS.map((key) => ({
          value: key as Segment, label: labels[key], icon: MOBILE_LAYOUT_ICONS[key],
        })),
        { value: 'table' as Segment, label: t('toolbar.table'), icon: <TableIcon width={14} height={14} /> },
      ]}
    />
  );
}
